import useToast from "hooks/common/useToast";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ToastPayload } from "types/toast";
import { domMax, LazyMotion, m } from "framer-motion";

const Toast = ({ id, toast }: { id: string, toast: ToastPayload }) => {
    const { remove } = useToast();
    const { text, options, mountTo } = toast;
    const { duration, type, position, onClick } = options;

    const backgroundColor = useMemo(() => {
      switch (type) {
        case 'default':
        default:
          return 'bg-toast-bg-default';
        case 'success':
          return 'bg-toast-bg-success';
        case 'warning':
          return 'bg-toast-bg-warning';
      }
    }, [type]);

    const textColor = useMemo(() => {
      switch (type) {
        case 'default':
        default:
          return 'text-toast-txt-default';
            case 'success':
          return 'text-toast-txt-success';
        case 'warning':
          return 'text-toast-txt-warning';
      }
    }, [type]);

    useEffect(() => {
      const timer = setTimeout(() => {
        remove(id);
      }, duration);
      return () => clearTimeout(timer);
    }, [id, duration, remove]);

    const portalRoot = useMemo(() => mountTo ?? (typeof document !== 'undefined' ? document.getElementById('hMainContainer') : null), [mountTo]);
    if (!portalRoot) return null;

    // 컨테이너가 상태바 밑까지 차지하므로 --status-bar-height(main.js가 세팅)로 safe-area 보정.
    // 수치는 vanilla 토스트(uicommon.js showToast)와 동일하게 맞춤
    const positionStyle =
      position === 'bottom'
        ? { bottom: 'calc(var(--status-bar-height, 0px) + 72px)' }
        : { top: 'calc(var(--status-bar-height, 0px) + 16px)' };

    const content = (
      <LazyMotion features={domMax} strict>
        <m.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 32 }}
          data-component="Toast"
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`absolute left-4 right-4 ${backgroundColor} ${textColor} rounded-lg p-3`}
          style={{ zIndex: 102, ...positionStyle }}
          onClick={onClick}
        >
          <span className="typography-body-sm">{text}</span>
        </m.div>
      </LazyMotion>
    );

    return createPortal(content, portalRoot);
};

export default Toast;